import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  AbilityEffect,
  Condition,
  Potence,
  CharacterReceivable,
  CharacterReceivableItem,
  CharacterCondition
} from "@dndModels";
import { SmartHash } from "@models";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";
import DataUtilities from '@dndUtilities/data_utilities';
import { makeStyles } from "@utilities/theme_utilities";

import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import LoadingBox from "@components/input/LoadingBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DandDButtonBox from "@dndComponents/DandDButtonBox";

interface Props {
  character: Character;
  source_true_id: string;
  effect: AbilityEffect;
  potence: Potence;
  disabled?: boolean;
  shared: boolean;
  onChange: () => void;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterSelfConditionEffect({
  character,
  source_true_id,
  effect,
  potence,
  disabled = false,
  shared,
  // onChange,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [conditions, setConditions] = useState<Condition[]>([]);
  // const [ping, setPing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [changing, setChanging] = useState(false);
  const [selected_id, setSelectedId] = useState<string>("");
  const [condition_hash, setConditionHash] = useState<{ [id: string]: { level: number, source_true_id: string } }>({});
  const [open, setOpen] = useState(false);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        // margin: "4px",
        backgroundColor: theme.palette.secondary.main
      },
    });
  });

   
  useEffect(() => {
    if (!fetched) {
      load();
    }
  });

  useEffect(() => {
    if (changing) {
      // console.log("changing");
      setChanging(false);
      setProcessing(true);
      setTimeout(() => {
        character.changed();
        char_util.repopulateCharFeatureBases(character).then(() => {
          // console.log("done");
          setProcessing(false);
        });
      }, 1000);
    }
    // eslint-disable-next-line
  }, [changing]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
     
  }, [reloading, setReloading]);
  
  const enabled = shared || (!disabled && api.user && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id)));
    
  const copyHash = () => {
    const new_hash: { [id: string]: { level: number, source_true_id: string } } = {};
    for (const char_cond of character.conditions) {
      new_hash[char_cond.source_id] = { 
        level: char_cond.level, 
        source_true_id: char_cond.condition_source_true_id 
      };
    }
    // for (const key of Object.keys(character.condition_hash)) {
    //   new_hash[key] = {...character.condition_hash[key]};
    // }
    setConditionHash(new_hash);
  }

  const load = () => {
    setLoading(true);
    setFetched(true);
    const onComplete = (res: SmartHash) => {
      if (res && !res.error) {
        setConditions(res.condition as Condition[]);
        setLoading(false);
        copyHash();
      }
    };
    const onProgress = () => {};
    api.getSetOfObjectsWithCallbacks({ 
      data_types: ["condition"],
      onComplete, onProgress 
    });
  }

  const { classes, cx } = useStyles();
  
  const renderCharacters = (condition: Condition, level: number) => {
    const campaign = character.campaign;
    if (campaign) {
      return campaign.characters.filter(o => o.active && !o.is_archived && o._id !== character._id).map(char => {
        return (
          <Grid key={char._id} size={{
            xs: 12
          }}>
            <div className="padded">
              <Grid container spacing={0} direction="row" className={cx(classes.root, "padded")}>
                <Grid
                  size={{
                    xs: 9
                  }}>
                  { char.name }
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <DandDButtonBox 
                    name="Apply"
                    disabled={processing}
                    onClick={() => {
                      const receivable = new CharacterReceivable();
                      receivable.from_id = character._id;
                      receivable.char_id = char._id;
                      receivable.sent_dt = common_util.get_time_number();
                      receivable.items = [new CharacterReceivableItem({
                        type: "Condition",
                        obj_id: condition._id,
                        count: level,
                      })];
                      setProcessing(true);
                      api.upsertObject("character_receivable", receivable).then(res => {
                        setProcessing(false);
                      });
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        );
      });
    }
    return null;
  }

  const renderMinions = (condition: Condition, level: number) => {
    if (character instanceof Character) {
      return character.minions.map(minion => {
        return (
          <Grid key={minion.true_id} size={{
            xs: 12
          }}>
            <div className="padded">
              <Grid container spacing={0} direction="row" className={cx(classes.root, "padded")}>
                <Grid
                  size={{
                    xs: 9
                  }}>
                  { minion.name }
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <DandDButtonBox 
                    name="Apply"
                    disabled={processing}
                    onClick={ async () => {
                      let cc = minion.conditions.find(o => o.source_id === condition._id);
                      if (!cc) {
                        await api.loadObject(condition);
                        cc = new CharacterCondition();
                        cc.copy_source({ char: character, feature_source: condition });
                        cc.level = 1;
                        character.conditions.push(cc);
                      }
                      cc.level = level;
                      char_util.repopulateCharFeatureBases(character);
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        );
      });
    }
    return null;
  }
  
  const renderExtras = () => {
    const self_condition_ids = potence.conditions_applied;
    let condition: Condition | undefined = undefined;
    let level = 1;
    if (self_condition_ids.length === 1) {
      condition = conditions.find(o => o._id === self_condition_ids[0]);
      if (condition) {
        if (condition.max_level > 1) {
          level = char_util.value(potence.condition_levels_applied, { obj: character });
        } 
        if (potence.remove_condition_levels) {
          level = -1 * level;
        }
      } else {
        return <>Invalid Condition</>;
      }
    } else {
      condition = conditions.find(o => o._id === selected_id);
    }
    if (condition && level !== 0) {
      return (
        <ModalOrDrawer 
          open={ open } 
          full_page
          onClose={() => {
            setOpen(false); 
            character.subscription_holder.modalClosed();
          }}>
          { open &&
            <Grid container spacing={0} direction="row" 
              style={{ maxHeight: window.innerHeight - 34 }}>
              <Grid
                className="header centered"
                size={{
                  xs: 12
                }}>
                { level === 1 ? 
                  `Add ${condition.name} to Allies`
                : level === -1 ? 
                  `Remove ${condition.name} from Allies`
                : level < 0 ? 
                  `Remove ${level * -1} levels of ${condition.name} from Allies`
                :
                  `Add ${level} levels of ${condition.name} to Allies`
                }
              </Grid>
              { renderCharacters(condition, level) }
              { renderMinions(condition, level) }
            </Grid>
          }
        </ModalOrDrawer>
      );
    }
    return null;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const char = character;
    
    // const alternate_condition_features = char.alternate_condition_features.filter(o => potence.conditions_applied.includes(o.original_condition_id));
    
    const self_condition_ids = 
      // alternate_condition_features.length === 0 ? 
      potence.conditions_applied; // : potence.conditions_applied.map(o => {
    //   const alternate_condition_feature = alternate_condition_features.find(o2 => o2.original_condition_id === o);
    //   if (alternate_condition_feature) {
    //     return alternate_condition_feature.alternate_condition_id;
    //   } else {
    //     return o;
    //   }
    // });

    const renderApplyRemove = () => {
      const has_condition = char_util.hasCondition(char, [selected_id], 1);
      const has_any_condition = char_util.hasCondition(char, self_condition_ids, 1);
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <DandDButtonBox
              name={ 
                selected_id === "" ? 
                  "First Select a Condition" : 
                  (has_condition ? 
                    "Remove" : 
                    "Apply"
                  ) 
              }
              style={{
                fontSize: "9px",
              }}
              disabled={ !enabled || selected_id === "" || processing }
              onClick={ async () => {
                const cc = char.conditions.find(o => o.source_id === selected_id);
                if (cc) {
                  char_util.removeCharFeatureBasesFor(character, cc);
                  character.conditions = character.conditions.filter(o => o.source_id !== cc.source_id);
                } else {
                  const obj = data_util.conditions.find(o => o._id === selected_id);
                  if (obj) {
                    await api.loadObject(obj);
                    const cc = new CharacterCondition();
                    cc.copy_source({ char: character, feature_source: obj });
                    cc.level = 1;
                    character.conditions.push(cc);
                  }
                }
                copyHash();
                character.changed();
                char_util.repopulateCharFeatureBases(character);
              }} 
            />
          </Grid>
          { has_any_condition && 
            <Grid
              size={{
                xs: 12
              }}>
              <DandDButtonBox
                name="Remove Any"
                style={{
                  fontSize: "9px",
                }}
                disabled={ !enabled || processing }
                onClick={ async () => {
                  const ccs = char.conditions.filter(o => self_condition_ids.includes(o.source_id));
                  for (const cc of ccs) {
                    char_util.removeCharFeatureBasesFor(character, cc);
                    character.conditions = character.conditions.filter(o => o.source_id !== cc.source_id);
                  }
                  copyHash();
                  character.changed();
                  char_util.repopulateCharFeatureBases(character);
                }} 
              />
            </Grid>
          }
        </Grid>
      );
    }
    
    // debugger;
    if (self_condition_ids.length === 0) {
      return null;
    } else if (self_condition_ids.length === 1) {
      const condition = conditions.find(o => o._id === self_condition_ids[0]);
      if (condition) {
        if (condition.max_level > 1) {
          let levels_applied = char_util.value(potence.condition_levels_applied, { obj: char });
          if (potence.remove_condition_levels) {
            levels_applied = -1 * levels_applied;
          }
          const current_level = condition_hash[self_condition_ids[0]] ? condition_hash[self_condition_ids[0]].level : 0;
          return (
            <Grid container spacing={0} direction="row" 
              // style={{ height: "37.5px" }}
              >
              <Grid
                size={{
                  xs: 12
                }}>
                <DandDButtonBox
                  name={ levels_applied >= 0 ? `Apply ${levels_applied} ${condition.name}` : `Remove ${-1 * levels_applied} ${condition.name}` }
                  disabled={ !enabled || (levels_applied > 0 && current_level >= condition.max_level) || (levels_applied < 0 && current_level <= 0) || processing }
                  onClick={ async () => {
                    // const alternate_condition_features = char.alternate_condition_features.filter(o => potence.conditions_applied.includes(o.original_condition_id));
                    
                    const cond_ids = 
                      // alternate_condition_features.length === 0 ? 
                      potence.conditions_applied; // : potence.conditions_applied.map(o => {
                    //   const alternate_condition_feature = alternate_condition_features.find(o2 => o2.original_condition_id === o);
                    //   if (alternate_condition_feature) {
                    //     return alternate_condition_feature.alternate_condition_id;
                    //   } else {
                    //     return o;
                    //   }
                    // });
                    for (const cond_id of cond_ids) {
                      // Apply the condition
                      const obj = data_util.conditions.find(o => o._id === cond_id);
                      if (obj) {
                        await api.loadObject(obj);
                        const cc = new CharacterCondition();
                        cc.copy_source({ char: character, feature_source: obj });
                        cc.level = levels_applied;
                        character.conditions.push(cc);
                      }
                    }
                    copyHash();
                    character.changed();
                    char_util.repopulateCharFeatureBases(character);
                  }} 
                />
              </Grid>
              { current_level > 0 && levels_applied > 0 &&
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DandDButtonBox
                    name={ `Remove ${levels_applied} ${condition.name}` }
                    disabled={ !enabled || processing }
                    onClick={() => {
                      const cond_ids = 
                        potence.conditions_applied; 
                      for (const cond_id of cond_ids) {
                        const cc = char.conditions.find(o => o.source_id === cond_id);
                        if (cc) {
                          char_util.removeCharFeatureBasesFor(character, cc);
                          character.conditions = character.conditions.filter(o => o.source_id !== cc.source_id);
                        }
                      }
                      copyHash();
                      character.changed();
                      char_util.repopulateCharFeatureBases(character);
                    }} 
                  />
                </Grid>
              }
              { char instanceof Character && effect.target_int.target_type !== "Self" && (char.minions.length > 0 || char.campaign) &&
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DandDButtonBox
                    name="Apply to Ally"
                    disabled={ !enabled || processing }
                    onClick={() => {
                      setOpen(true);
                    }}
                  />
                  { renderExtras() }
                </Grid>
              }
            </Grid>
          );
        } else {
          const label = condition_hash[self_condition_ids[0]] ? 
            `Remove ${condition.name}` : 
            `Apply ${condition.name}`;
          return (
            <Grid container spacing={0} direction="row">
              <Grid
                className="centered"
                style={{ height: "37.5" }}
                size={{
                  xs: 12
                }}>
                <DandDButtonBox
                  name={ label }
                  disabled={ !enabled || processing }
                  onClick={ async () => {
                    // const alternate_condition_features = char.alternate_condition_features.filter(o => potence.conditions_applied.includes(o.original_condition_id));
                    
                    const cond_ids = 
                      // alternate_condition_features.length === 0 ? 
                      potence.conditions_applied; // : potence.conditions_applied.map(o => {
                    //   const alternate_condition_feature = alternate_condition_features.find(o2 => o2.original_condition_id === o);
                    //   if (alternate_condition_feature) {
                    //     return alternate_condition_feature.alternate_condition_id;
                    //   } else {
                    //     return o;
                    //   }
                    // });
                    for (const cond_id of cond_ids) {
                      const cc = char.conditions.find(o => o.source_id === cond_id);
                      if (cc) {
                        char_util.removeCharFeatureBasesFor(character, cc);
                        character.conditions = character.conditions.filter(o => o.source_id !== cc.source_id);
                        // character.spells = character.spells.filter(o => o.source_type !== "Gift" || o.source_id !== char_gift.source_id);
                        // onChange();
                      } else {
                        const obj = data_util.conditions.find(o => o._id === cond_id);
                        if (obj) {
                          await api.loadObject(obj);
                          const cc = new CharacterCondition();
                          cc.copy_source({ char: character, feature_source: obj });
                          cc.level = 1;
                          character.conditions.push(cc);
                          
                          // onChange();
                          // setPing(!ping);
                        }
                      }
                    }
                    copyHash();
                    character.changed();
                    char_util.repopulateCharFeatureBases(character);
                  }} 
                />
              </Grid>
            </Grid>
          );
        }
      } else {
        return <Grid>Invalid Condition</Grid>;
      }
    } else {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            className="centered"
            size={{
              xs: 6
            }}>
            <SelectObjectBox 
              name="Condition"
              data_type="condition"
              filter={{ _id: { "in": self_condition_ids }}}
              // log_it
              value={selected_id}
              onChange={setSelectedId}
            />
          </Grid>
          <Grid
            className="centered"
            size={{
              xs: 6
            }}>
            { renderApplyRemove() }
          </Grid>
        </Grid>
      );
    }
  }
}
